import { motion, useReducedMotion } from "framer-motion";
import type { HTMLMotionProps } from "framer-motion";
import { forwardRef } from "react";

type ButtonVariant = "primary" | "secondary" | "tertiary" | "ghost" | "danger";

interface ButtonProps extends HTMLMotionProps<"button"> {
  variant?: ButtonVariant;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: "bg-accent text-[var(--text-on-accent)] hover:bg-[var(--accent-hover)]",
  secondary: "bg-[var(--text-primary)] text-[var(--bg-app)] hover:opacity-90",
  tertiary: "bg-transparent text-accent border border-accent hover:bg-accent-soft",
  ghost: "bg-transparent text-accent hover:bg-accent-soft",
  danger: "bg-danger text-white hover:opacity-90",
};

/**
 * Carbon-style button primitive: square corners, 12px/16px padding,
 * IBM Plex Sans button type, subtle hover transition (respects reduced motion).
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = "primary", className = "", children, ...props }, ref) => {
    const prefersReduced = useReducedMotion();

    return (
      <motion.button
        ref={ref}
        whileHover={prefersReduced ? undefined : { y: -1 }}
        whileTap={prefersReduced ? undefined : { y: 0 }}
        transition={{ duration: 0.15, ease: "easeOut" }}
        className={`inline-flex items-center justify-center gap-2 rounded-none px-4 py-3 font-body text-sm font-normal tracking-[0.16px] transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-[var(--disabled-opacity)] ${VARIANT_CLASSES[variant]} ${className}`}
        {...props}
      >
        {children}
      </motion.button>
    );
  },
);

Button.displayName = "Button";

export default Button;
